import React from 'react';

import { RouteObject } from 'react-router-dom';
import {
  IndexRouteObject,
  NonIndexRouteObject,
} from 'react-router/dist/lib/context';

import {
  BreadcrumbComponentType,
  BreadcrumbsRoute,
} from 'use-react-router-breadcrumbs';

import { Actions, SubjectTypes } from '../auth/types/ability';
import { IAuthorizedRoute } from '../auth/types/can';

import { PropertiesList } from '../features/properties/properties-list';
import { PropertyDetail } from '../features/properties/property-detail';
import { PropertyView } from '../features/properties/ui/view/property-view';
import {
  PropertyDetailBreadcrumb,
  // EditPropertyDetailBreadcrumb,
} from '../features/properties/property-detail-breadcrumbs';

import { PropertyDetailHandler } from '../features/properties/ui/property-detail-handler';
import { UnitsMain } from '../features/units/units-main';
import { AlertTriggerDetailHandler } from '../features/properties/ui/alert-trigger-detail-handler';
import { PropertyContextWrapper } from '../features/properties/PropertyContextWrapper';
import { PropertyAlertTriggerDetail } from '../features/properties/property-alert-trigger-detail';
import { PropertyBuildingDetail } from '../features/properties/property-building-detail';
import { UnitDetailHandler } from '../features/units/ui/unit-detail-handler';
import { UnitView } from '../features/units/ui/unit-view';
import { BuildingDetailHandler } from '../features/properties/ui/building-detail-handler';
import { PropertyScheduleList } from '../features/properties/components/property-schedule-list';
import { ScheduleTemplateList } from '../features/units/schedule/views/schedule-template-list';
import { ScheduleDetailHandler } from '../features/units/schedule/views/schedule-detail-handler';
import { UnitsList } from '../features/units/units-list/units-list';
import { UnitDetail } from '../features/units/unit-detail';
import UnitCharts from '../features/units/charts/unit-charts';
import { UnitSchedule } from '../features/units/schedule/unit-schedule';
import { UnitPlmCalendar } from '../features/plm/plmCalendar/UnitPlmCalendar';
import { InstallerTools } from '../features/installer/installer-tools';
import { PropertyBuildingsList } from '../features/properties/property-buildings-list';
import { DeviceOfflineReport } from '../features/properties/device-offline-report';
import { useAuthorizer } from '../auth/AuthorizationContext';

type ParentBreadcrumbsRoute<ParamKey extends string = string> = (RouteObject &
  IAuthorizedRoute) & {
  breadcrumb?: BreadcrumbComponentType<ParamKey> | string | null;
} & {
  children: (
    | (IndexRouteObject & {
        authAction?: Actions;
        authType: SubjectTypes;
        breadcrumb?: BreadcrumbComponentType<ParamKey> | string | null;
        state?: any;
      })
    | (NonIndexRouteObject & {
        authAction?: Actions;
        authType?: SubjectTypes;
        breadcrumb?: BreadcrumbComponentType<ParamKey> | string | null;
        state?: any;
      })
  )[];
};

const SuperOnlyUnitPlmCalendar = () => {
  const { can } = useAuthorizer();
  return can('be', 'super') ? <UnitPlmCalendar /> : null;
};

export const propertyRoutes: ParentBreadcrumbsRoute[] = [
  {
    path: '/',
    element: <PropertiesList />,
    authType: 'Property',
    children: [],
  },
  {
    path: ':id',
    element: <PropertyContextWrapper />,
    children: [
      {
        index: true,
        element: <PropertyDetail />,
        authType: 'Property',
        authAction: 'view',
        state: {
          ignore: [
            /^\/properties\/[a-zA-Z0-9]+\/$/,
            /^\/properties\/[a-zA-Z0-9]+$/,
          ],
        },
      },
      {
        path: 'summary',
        element: <PropertyDetail />,
        authType: 'Property',
        authAction: 'view',
      },
      {
        path: 'alerts',
        element: <PropertyDetail />,
        authType: 'Property',
        authAction: 'view',
      },
      {
        path: 'reports/device-offline',
        element: <DeviceOfflineReport scope="property" />,
        authType: 'Property',
        authAction: 'view',
      },
      {
        path: 'view-property',
        element: <PropertyView />,
        authType: 'Property',
        authAction: 'view',
      },
      {
        path: 'schedule',
        element: <PropertyScheduleList />,
        authType: 'Property',
        authAction: 'view',
      },
      {
        path: 'schedule/templates',
        element: <ScheduleTemplateList mode="manage" />,
        authType: 'Property',
        authAction: 'view',
      },
      {
        path: 'schedule/templates/add',
        element: <ScheduleDetailHandler />,
        authType: 'Property',
        authAction: 'view',
      },
      {
        path: 'schedule/templates/:templateId/edit',
        element: <ScheduleDetailHandler />,
        authType: 'Property',
        authAction: 'view',
      },
      {
        path: 'edit-property',
        element: <PropertyDetailHandler />,
        authType: 'Property',
        authAction: 'update',
      },
      {
        path: 'edit-property/alerts/alert-triggers/:tid/edit-alert-trigger',
        element: <AlertTriggerDetailHandler />,
        authType: 'AlertTrigger',
        authAction: 'update',
      },
      {
        path: 'edit-property/alerts/alert-triggers/:tid/view-alert-trigger',
        element: <PropertyAlertTriggerDetail />,
        authType: 'AlertTrigger',
        authAction: 'view',
      },
      {
        path: 'edit-property/:viewId',
        element: <PropertyDetailHandler />,
        authType: 'Property',
        authAction: 'update',
      },
      {
        path: 'edit-property/:viewId/:alertViewId',
        element: <PropertyDetailHandler />,
        authType: 'Property',
        authAction: 'update',
      },
      {
        path: 'buildings',
        element: <PropertyBuildingsList />,
        authType: 'Property',
        authAction: 'view',
      },
      {
        path: 'buildings/:bid',
        element: <PropertyBuildingDetail />,
        authType: 'Building',
        authAction: 'view',
      },
      {
        path: 'units/add-unit',
        element: <UnitDetailHandler />,
        authType: 'Property',
        authAction: 'createUnit',
      },
      {
        path: 'units/:unitId/edit-unit',
        element: <UnitDetailHandler />,
        authType: 'Unit',
        authAction: 'update',
      },
      {
        path: 'units/:unitId/edit-unit/:viewId',
        element: <UnitDetailHandler />,
        authType: 'Unit',
        authAction: 'update',
      },
      {
        path: 'units/:unitId/view-unit',
        element: <UnitView />,
        authType: 'Property',
        authAction: 'view',
      },
      {
        path: 'buildings/add-building',
        element: <BuildingDetailHandler />,
        authType: 'Property',
        authAction: 'createBuilding',
      },
      {
        path: 'buildings/:bid/edit-building',
        element: <BuildingDetailHandler />,
        authType: 'Property',
        authAction: 'update',
      },
      {
        path: 'installer',
        element: <UnitsMain />,
        authType: 'Property',
        authAction: 'view',
        children: [
          {
            index: true,
            element: <UnitsList installerView={true} />,
          },
          {
            path: ':unitId',
            element: <UnitDetail installerView={true} />,
          },
          {
            path: ':unitId/charts',
            element: <UnitCharts />,
          },
          {
            path: ':unitId/schedule',
            element: <UnitSchedule />,
          },
          {
            path: ':unitId/plm-schedule',
            element: <SuperOnlyUnitPlmCalendar />,
          },
          {
            path: ':unitId/:deviceId',
            element: <InstallerTools />,
          },
        ],
      },
      {
        path: 'units',
        element: <UnitsMain />,
        authType: 'Property',
        authAction: 'view',
        children: [
          {
            index: true,
            element: <UnitsList />,
          },
          {
            path: ':unitId',
            element: <UnitDetail />,
          },
          {
            path: ':unitId/charts',
            element: <UnitCharts />,
          },
          {
            path: ':unitId/schedule',
            element: <UnitSchedule />,
          },
          {
            path: ':unitId/plm-schedule',
            element: <SuperOnlyUnitPlmCalendar />,
          },
        ],
      },
    ],
  },
  {
    path: 'create',
    element: <PropertyDetailHandler />,
    authType: 'Property',
    authAction: 'create',
    children: [],
  },
];

export const propertyBreadcrumbRoutes: (BreadcrumbsRoute & { state?: any })[] =
  [
    {
      path: '/properties',
      element: <PropertiesList />,
      breadcrumb: () => <span>Properties</span>,
    },
    {
      path: '/properties/create',
      element: <PropertyDetailHandler />,
      breadcrumb: () => <span>Create Property</span>,
    },
    {
      path: '/properties/:id',
      element: <PropertyDetail />,
      breadcrumb: ({
        match: {
          params: { id },
        },
      }) => <PropertyDetailBreadcrumb id={id} />,
      state: {
        ignore: [
          /^\/properties\/[a-zA-Z0-9]+$/,
          /^\/properties\/[a-zA-Z0-9]+\/$/,
        ],
      },
    },
    // TODO: Peter: do we need this?
    // {
    //   path: '/properties/:id/edit-property',
    //   element: <PropertyDetailHandler />,
    //   breadcrumb: ({
    //     match: {
    //       params: { id },
    //     },
    //   }) => <EditPropertyDetailBreadcrumb id={id} />,
    // },
    {
      path: '/properties/:id/installer',
      element: <UnitsMain />,
      breadcrumb: () => <span>Units Installer List</span>,
      state: {
        ignore: [
          /^\/properties\/[a-zA-Z0-9]+\/installer$/,
          /^\/properties\/[a-zA-Z0-9]+\/installer\/$/,
        ],
      },
    },
  ];
